/**
 * This file defines the InviteManagerDialog component, designed to facilitate the
 * invitation of fund managers through a dialog interface. It incorporates input validation,
 * controlled form inputs, and submission logic, leveraging custom hooks for form management
 * and authentication.
 *
 */
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import * as React from 'react';
import { DialogInput } from './DialogInput';
import { useForm } from '../../hooks/form';
import { FormContainer } from './FormContainer';
import PhoneNumberInput from './PhoneNumberInput';
import { useUser } from '../../hooks/user';
import {
    getUserActionSubmitConfig,
    validateEmailAddress,
    validatePhoneNumber
} from '../../functions/forms';
import { UserActionType } from '../menus/menuItemsConfig';
import { Stack } from '@mui/system';
import theme from '../../styles/theme';

const requiredFields = ['fundName', 'fundContactName'];

const validationConfig = {
    phoneNumber: {
        validate: validatePhoneNumber,
        additionalValidationArguments: ['countryCode']
    },
    fundContactEmail: {
        validate: validateEmailAddress
    }
};

const radioFormTextStyle = {
    fontSize: '0.875rem',
    color: theme.palette.disabled.darkest
};

const initialFormState = {
    fundName: { value: '', error: false, clicked: false },
    fundContactName: { value: '', error: false, clicked: false },
    fundContactEmail: { value: '', valid: true },
    phoneNumber: { countryCode: '1', value: '+1', error: false, valid: true },
    expectingContact: { value: 'true' },
    comments: { value: '' }
};

/**
 * Component for inviting a manager, rendered as a dialog containing multiple input fields.
 * It captures information about the fund and contact person, including validation of input data.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls the visibility of the dialog
 * @param {Function} props.handleFormClose - Function to close the Invite Manager dialog.
 * @param {Function} props.handleSubmissionViewClose - Function called when Submission View close button selected
 * @returns {React.ReactElement} The rendered InviteManagerDialog component
 */
const InviteManagerDialog = ({
    open,
    handleFormClose,
    handleSubmissionViewClose
}) => {
    const formId = UserActionType.INVITE_MANAGER;

    const { user } = useUser(); // Assuming useUser hook is accessible or you pass the user context as a prop

    const submitConfig = getUserActionSubmitConfig(formId, user);

    const {
        formState,
        setFormState,
        resetFields,
        setFormField,
        handleSend,
        setFormFieldClicked,
        submissionState,
        isErrorAndClicked,
        setPhoneNumber
    } = useForm(
        initialFormState,
        requiredFields,
        validationConfig,
        submitConfig
    );

    /**
     * Renders form inputs for fund information and contact details, including name, email,
     * phone number, and additional comments. Uses DialogInput and PhoneNumberInput components
     * for input fields.
     */
    const formFields = (
        <Stack spacing={3} padding={2}>
            <DialogInput
                id='fundName'
                label='Fund name'
                value={formState.fundName.value}
                required
                onChange={(e) => setFormField('fundName', e.target.value)}
                error={isErrorAndClicked('fundName')}
                onBlur={() => setFormFieldClicked('fundName')}
                data-analyticsid='fund-name'
            />
            <DialogInput
                id='fundContactName'
                label='Fund contact name'
                value={formState.fundContactName.value}
                required
                onChange={(e) =>
                    setFormField('fundContactName', e.target.value)
                }
                error={isErrorAndClicked('fundContactName')}
                onBlur={() => setFormFieldClicked('fundContactName')}
                data-analyticsid='fund-contact-name'
            />
            <DialogInput
                id='fundContactEmail'
                label='Fund email'
                value={formState.fundContactEmail.value}
                onChange={(e) =>
                    setFormState({
                        ...formState,
                        fundContactEmail: {
                            ...formState.fundContactEmail,
                            value: e.target.value,
                            valid: true
                        }
                    })
                }
                error={!formState.fundContactEmail.valid}
                helperText={
                    formState.fundContactEmail.valid
                        ? ' '
                        : 'Invalid email address'
                }
                type={'email'}
                data-analyticsid='fund-email'
            />
            <PhoneNumberInput
                value={formState.phoneNumber.value}
                onChange={setPhoneNumber}
                error={formState.phoneNumber.error}
                valid={formState.phoneNumber.valid}
                placeholder='Fund contact phone number'
                style={{ marginTop: '4px' }} //manual Stack spacing override to account for helperText above
                data-analyticsid='fund-phone-number'
            />
            <FormControl
                sx={{ pl: 2 }}
                style={{ marginTop: '6px' }}
                data-analyticsid='expecting-us'
            >
                <FormLabel required sx={radioFormTextStyle}>
                    Is this person expecting to hear from us?
                </FormLabel>
                <RadioGroup
                    row
                    defaultValue={initialFormState.expectingContact.value}
                    onChange={(e) =>
                        setFormField('expectingContact', e.target.value)
                    }
                >
                    <FormControlLabel
                        value={'true'}
                        control={<Radio />}
                        label={
                            <Typography sx={radioFormTextStyle}>Yes</Typography>
                        }
                    />
                    <FormControlLabel
                        value={'false'}
                        control={<Radio />}
                        label={
                            <Typography sx={radioFormTextStyle}>No</Typography>
                        }
                    />
                </RadioGroup>
            </FormControl>
            <DialogInput
                id={'comments'}
                value={formState.comments.value}
                label='Any comments?'
                onChange={(e) => setFormField('comments', e.target.value)}
                multiline // Allow multiple lines
                rows={4} // Number of rows when multiline is true
                data-analyticsid='comments'
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '20px' // Adjust this value to increase or decrease roundness
                    }
                }}
            />
        </Stack>
    );

    return (
        <FormContainer
            formId={formId}
            formState={formState}
            open={open}
            handleClose={handleFormClose}
            title='Invite a Manager'
            submissionView={"Thank you!\nWe'll be in touch with them soon"}
            onSubmissionViewClose={handleSubmissionViewClose}
            requiredFields={requiredFields}
            handleSend={handleSend}
            submissionState={submissionState}
            resetFields={resetFields}
            dataAnalyticsId='invite-manager-form'
        >
            {formFields}
        </FormContainer>
    );
};

InviteManagerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleFormClose: PropTypes.func.isRequired,
    handleSubmissionViewClose: PropTypes.func
};
export default InviteManagerDialog;
